import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import { ShoppingCart, ArrowLeft, Package, Flame, Clock, Leaf } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${quantity}x ${product.name} added to cart!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-20 text-center">
          <div className="inline-block w-12 h-12 border-4 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-lime-400 mb-8 transition-colors"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            {product.badge && (
              <div className="mb-4">
                <span className="bg-orange-500 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest">
                  {product.badge}
                </span>
              </div>
            )}
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-auto border border-zinc-800"
              data-testid="product-image"
            />
          </div>

          <div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              {product.name}
            </h1>
            <p className="text-zinc-300 text-lg leading-relaxed mb-6">
              {product.description}
            </p>

            <div className="flex items-center gap-3 mb-6">
              <span className="text-5xl font-bold text-lime-400" data-testid="product-price">
                ₹{product.price}
              </span>
              {product.original_price && (
                <span className="text-2xl text-zinc-500 line-through">
                  ₹{product.original_price}
                </span>
              )}
              {product.original_price && (
                <span className="bg-orange-500 text-white px-2 py-1 text-sm font-bold">
                  SAVE {Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
                </span>
              )}
            </div>

            <div className="flex gap-2 mb-8 flex-wrap">
              {product.features.map((feature, idx) => (
                <span
                  key={idx}
                  className="bg-lime-400/10 text-lime-400 border border-lime-400/20 px-3 py-1.5 text-sm font-mono uppercase"
                >
                  {feature}
                </span>
              ))}
            </div>

            <div className="border border-zinc-800 p-6 bg-zinc-900 mb-6">
              <h3 className="font-heading text-xl font-semibold uppercase mb-4">Key Benefits</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-lime-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Weight: {product.weight_kg}kg</p>
                    <p className="text-sm text-zinc-400">Compressed sawdust briquettes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Flame className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">High Heat Output</p>
                    <p className="text-sm text-zinc-400">3800-4200 kcal/kg energy</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-lime-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Long Burn Time</p>
                    <p className="text-sm text-zinc-400">3-4 hours per kg</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Leaf className="w-5 h-5 text-lime-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Eco-Friendly</p>
                    <p className="text-sm text-zinc-400">Made from agricultural waste</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 items-center mb-6">
              <div className="flex items-center gap-2">
                <label className="text-sm uppercase tracking-wider text-zinc-400 font-semibold">
                  Quantity:
                </label>
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 bg-zinc-800 hover:bg-zinc-700 text-white font-bold border border-zinc-700"
                  data-testid="decrease-quantity"
                >
                  -
                </button>
                <span className="w-12 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center font-bold" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 bg-zinc-800 hover:bg-zinc-700 text-white font-bold border border-zinc-700"
                  data-testid="increase-quantity"
                >
                  +
                </button>
              </div>
              <div className="text-zinc-400 text-sm">
                Total: <span className="text-lime-400 font-bold text-lg">₹{product.price * quantity}</span>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleAddToCart}
                className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                Add to Cart
              </button>
              <Link
                to="/cart"
                className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-8 py-4 transition-colors"
                data-testid="go-to-cart-btn"
              >
                Go to Cart
              </Link>
            </div>

            <div className="mt-6 p-4 bg-lime-400/10 border border-lime-400/20">
              <p className="text-lime-400 font-semibold mb-1">✅ Same-day delivery in Bengaluru</p>
              <p className="text-zinc-400 text-sm">Order now and get it delivered today!</p>
            </div>
          </div>
        </div>

        <div className="mt-16 border border-zinc-800 p-8 bg-zinc-900">
          <h2 className="font-heading text-3xl font-bold uppercase mb-6">How to Use</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className="w-12 h-12 bg-lime-400 text-black font-bold text-xl flex items-center justify-center mb-3">
                1
              </div>
              <h3 className="font-semibold text-white mb-2">Setup</h3>
              <p className="text-zinc-400 text-sm">Place 4-6 briquettes in stove base (chimney/angithi style best)</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-lime-400 text-black font-bold text-xl flex items-center justify-center mb-3">
                2
              </div>
              <h3 className="font-semibold text-white mb-2">Light</h3>
              <p className="text-zinc-400 text-sm">Newspaper/kitchen paper underneath → 8-10 mins to glowing embers</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-lime-400 text-black font-bold text-xl flex items-center justify-center mb-3">
                3
              </div>
              <h3 className="font-semibold text-white mb-2">Cook</h3>
              <p className="text-zinc-400 text-sm">1kg briquettes = 1kg meat/1.5kg rice (2-3hr cooking session)</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-lime-400 text-black font-bold text-xl flex items-center justify-center mb-3">
                4
              </div>
              <h3 className="font-semibold text-white mb-2">Cleanup</h3>
              <p className="text-zinc-400 text-sm">Ash into plants (natural fertilizer) - no sooty mess</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
